import { useAssetsStore } from '@/stores/assets'
import { showConfirm } from './dialog'
import router from '@/router'

/**
 * 单独验证手机号绑定
 *
 * @throws 如果验证失败或用户取消，抛出异常
 */
export async function verifyPhoneBind(): Promise<void> {
  const assetsStore = useAssetsStore()
  const userProfile = assetsStore.userProfile

  if (!userProfile) {
    await assetsStore.refreshProfile()
  }

  if (assetsStore.userProfile?.phone_bind !== true) {
    const result = await showConfirm('安全提示', '为了您的安全，请您前往绑定手机号', {
      confirmText: '前往认证',
    })
    if (result) {
      await router.push({ name: 'BindPhoneStep1', query: { bind: 'phone' } })
      throw new Error('需要绑定手机号')
    } else {
      throw new Error('需要绑定手机号')
    }
  }
}

/**
 * 单独验证实名认证
 *
 * @throws 如果验证失败或用户取消，抛出异常
 */
export async function verifyKYC(): Promise<void> {
  const assetsStore = useAssetsStore()
  const userProfile = assetsStore.userProfile

  if (!userProfile) {
    await assetsStore.refreshProfile()
  }

  if (assetsStore.userProfile?.kyc_verified !== true) {
    const result = await showConfirm('安全提示', '您未进行身份验证，买卖币需要进行认证才可以交易哦', {
      confirmText: '前往认证',
    })
    if (result) {
      await router.push({ name: 'VerifyReal' })
      throw new Error('需要实名认证')
    } else {
      throw new Error('需要实名认证')
    }
  }
}

/**
 * 单独验证支付密码
 *
 * @throws 如果验证失败或用户取消，抛出异常
 */
export async function verifyPaymentPassword(): Promise<void> {
  const assetsStore = useAssetsStore()
  const userProfile = assetsStore.userProfile

  if (!userProfile) {
    await assetsStore.refreshProfile()
  }

  if (assetsStore.userProfile?.has_pin !== true) {
    const result = await showConfirm('温馨提示', '您还未设置支付密码哦', {
      confirmText: '前往设置',
    })
    if (result) {
      await router.push('/user/set-pin')
      throw new Error('需要设置支付密码')
    } else {
      throw new Error('需要设置支付密码')
    }
  }
}

/**
 * 单独验证收付款方式
 *
 * @throws 如果验证失败或用户取消，抛出异常
 */
export async function verifyPaymentMethod(): Promise<void> {
  const assetsStore = useAssetsStore()
  const userProfile = assetsStore.userProfile

  if (!userProfile) {
    await assetsStore.refreshProfile()
  }

  if (assetsStore.userProfile?.has_pay_method !== true) {
    const result = await showConfirm(
      '需要设置收付款方式',
      '请先添加至少一个收付款方式以便进行交易',
      {
        confirmText: '去设置',
      },
    )
    if (result) {
      await router.push('/user/pay-methods')
      throw new Error('需要设置收付款方式')
    } else {
      throw new Error('需要设置收付款方式')
    }
  }
}

/**
 * 用户交易前验证
 * 逐步验证：先手机号 → 再实名(KYC) → 支付密码 → 收付款方式。
 * 某一项未通过时弹窗提示，用户选择「去绑定/去认证」则跳转对应页面并中止，不再执行后续验证。
 *
 * @throws 任何一项验证未通过或用户取消时抛出异常
 *
 * @example
 * ```ts
 * try {
 *   await verifyUserBeforeTrade()
 *   // 所有验证通过，继续交易流程
 * } catch (error) {
 *   // 验证失败，已跳转或已阻断
 * }
 * ```
 */
export async function verifyUserBeforeTrade(): Promise<void> {
  await verifyPhoneBind() // 未绑定手机 → 跳转手机绑定页并中止
  await verifyKYC() // 未实名 → 跳转 KYC 页并中止
  await verifyPaymentPassword()
  await verifyPaymentMethod()
}

export async function verifyUserBeforeBuyCoin(): Promise<void> {
  await verifyPhoneBind() // 未绑定手机 → 跳转手机绑定页并中止
  await verifyKYC() // 未实名 → 跳转 KYC 页并中止
  await verifyPaymentMethod()
}